import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { get } from 'lodash';
import classNames from 'classnames';

import { msg } from 'api';

import { noop } from 'utils/func';
import NavigationBanners from './NavigationBanners';
import PersonalSection from './PersonalSection';
import PromosLinks from './PromosLinks';
import LevelItemsList from './LevelItemsList';

import PageEvents from '../models/PageEvents';
import PageEventFields from '../models/PageEventFields';
import wa from 'apps/analytics/services/waEventEmitter';
import { gtm } from 'apps/gtm';

export default class FoldedNavigation extends Component {
  static propTypes = {
    user: PropTypes.object,
    promos: PropTypes.array,
    hidePromoSection: PropTypes.bool,
    hidePersonalItems: PropTypes.bool,
    hidePromoItems: PropTypes.bool,
    levelIds: PropTypes.array.isRequired,
    levels: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        categories: PropTypes.array,
      }),
    ),
    linksRoot: PropTypes.string,
    itemMouseOverAction: PropTypes.func,
    itemMouseOutAction: PropTypes.func,
    navMouseLeaveAction: PropTypes.func,
    levelMouseOverAction: PropTypes.func,
    itemTouchStartAction: PropTypes.func,
    userEventTypeRecognizer: PropTypes.func,
    collapse: PropTypes.func,
    hideBrandBanners: PropTypes.bool,
    inSidebar: PropTypes.bool,
    lastEventType: PropTypes.string,
    brandBanners: PropTypes.array,
    defaultBrandBannersActive: PropTypes.bool,
    productsPage: PropTypes.object,
    pageParameters: PropTypes.object,
    hideViewAll: PropTypes.bool,
  };

  static defaultProps = {
    hidePromoItems: false,
    hidePersonalItems: false,
    itemMouseOverAction: noop,
    itemMouseOutAction: noop,
    itemTouchStartAction: noop,
    navMouseLeaveAction: noop,
    levelMouseOverAction: noop,
    userEventTypeRecognizer: noop,
    collapse: noop, // must be defined because without noop HMR puts some garbage here
    productsPage: {},
  };

  shouldComponentUpdate(nextProps) {
    return (
      this.props.productsPage !== nextProps.productsPage
      || this.props.levelIds !== nextProps.levelIds
    );
  }

  componentDidUpdate(prevProps) {
    const { levelIds } = this.props;
    // Transition: levelIds zero -> non-zero or non-zero -> zero
    if (!levelIds.length !== !prevProps.levelIds.length) {
      if (levelIds.length) {
        // Menu is unfolded
        window.appContainer.addEventListener('touchstart', this.onAppContainerTouchStart);
      } else {
        // Menu is folded
        window.appContainer.removeEventListener('touchstart', this.onAppContainerTouchStart);
      }
    }
  }

  trackItemClick = (clickedCategoryId, levelIdx) => {
    wa.triggerPageEventWithDescription(
      this.props.inSidebar ? PageEvents.LEFT_SIDEBAR_CLICKED : PageEvents.BROWSE_DROPDOWN_CLICKED,
      { [PageEventFields.LEVEL_IDX]: levelIdx, [PageEventFields.CATEGORY]: clickedCategoryId }
    );
  };

  // fold navigation if tap registered outside folded navigation wrapper
  onAppContainerTouchStart = (event) => {
    const clickedInside = this.container && this.container.contains(event.target);
    if (!clickedInside) {
      this.props.navMouseLeaveAction();
    }
  };

  onLeafItemMouseOver = (e) => {
    this.props.itemMouseOverAction(e, []);
  };

  onItemMouseClick = (e, clickedCategoryId, levelIdx, clickedCategoryName) => {
    this.trackItemClick(clickedCategoryId, levelIdx);

    this.props.itemMouseOverAction(e, [], { allowDefault: true, clickedCategoryId });
    const categoryPath = [];
    // build nested category names
    for (let i = 1, len = levelIdx; i <= len; i++) {
      const { id, name } = this.props.levels[i];
      if (id) {
        if (id !== 'highlighted') {
          categoryPath.push(name);
        }
      }
    }
    categoryPath.push(clickedCategoryName);

    if (!this.props.inSidebar) {
      gtm.trigger('gtm:navigationEvent', {
        menuType: 'Main menu',
        menuMainItem: 'Browse Shop',
        menuSubItem: categoryPath.join(' > '),
      });
    }

    if (this.props.collapse && typeof this.props.collapse === 'function') {
      this.props.collapse();
    }
  };

  onFocus = (e) => {
    if (this.props.inSidebar) {
      return this.props.itemMouseOverAction(e, ['highlighted']);
    }
    return this.onLeafItemMouseOver(e);
  };

  onPromoLinkClick = (promoName, levelIdx) => (e) => { this.onItemMouseClick(e, promoName, levelIdx); };

  render() { // eslint-disable-line max-lines-per-function
    const {
      inSidebar,
      hideBrandBanners,
      levels,
      levelIds,
      promos,
      hidePromoSection,
      hidePromoItems,
      hidePersonalItems,
      lastEventType,
      brandBanners,
      brandBannersWideThreshold,
      defaultBrandBannersActive,
      user,
      productsPage,
      pageParameters,
      linksRoot,
      levelMouseOverAction,
      navMouseLeaveAction,
      itemMouseOutAction,
      userEventTypeRecognizer,
      itemTouchStartAction,
      itemMouseOverAction,
      hideViewAll,
    } = this.props;

    const wrapperClass = classNames(
      'folded-nav',
      inSidebar ? 'sidebar-nav' : 'browse-shop-nav',
      lastEventType && `${lastEventType}-events-visible`,
    );
    const topPicksClass = classNames(
      'level-item promotion has-children',
      { selected: levelIds[0] === 'highlighted' },
    );

    if (!levels || !levels[0].categories) {
      return null;
    }

    const [baseLevel, ...nestedLevels] = levels;

    const levelItemsListProps = {
      levelIds,
      levels,
      promos,
      linksRoot,
      pageParameters,
      onLeafItemMouseOver: this.onLeafItemMouseOver,
      onItemMouseClick: this.onItemMouseClick,
      onItemMouseOut: itemMouseOutAction,
      onLevelMouseOver: levelMouseOverAction,
      onItemTouchStartAction: itemTouchStartAction,
      onPromoLinkClick: this.onPromoLinkClick,
      itemMouseOverAction,
      hideViewAll,
    };

    return (
      <div
        className={wrapperClass}
        onMouseLeave={navMouseLeaveAction}
        // TODO: add throtting to limit dispatch spam
        // Handlers are empty methods until then
        onMouseMove={userEventTypeRecognizer}
        onTouchStart={userEventTypeRecognizer}
        onTouchMove={userEventTypeRecognizer}
        ref={ref => { this.container = ref; }}
      >
        <div className="level-base">
          {!hidePromoSection && (
            <div className="base-level promo-section">
              <ul>
                <li className={topPicksClass}>
                  <Link
                    className="level-item-link level-item-link--promo"
                    to="#"
                    onClick={e => e.preventDefault()}
                    onFocus={this.onFocus}
                    onMouseOver={this.onFocus}
                    onTouchStart={(e) => itemTouchStartAction(e, levelIds, ['highlighted'], 'highlighted')}
                  >
                    {msg.nav.labels.inspiration}
                  </Link>
                </li>
                {inSidebar && promos && (
                  <PromosLinks
                    levelIdx={0}
                    promos={promos}
                    onLeafItemMouseOver={this.onLeafItemMouseOver}
                    onPromoLinkClick={this.onPromoLinkClick}
                  />
                )}
              </ul>
            </div>
          )}
          <LevelItemsList
            {...levelItemsListProps}
            level={baseLevel}
            levelIdx={0}
            showPromoItems={false}
          />
          {!inSidebar && (
            <PersonalSection
              isSuggestedOrderAvailable={get(user, 'shoppingDetails.suggestedOrderAvailable')}
              hideSection={hidePersonalItems}
              onLeafItemMouseOver={this.onLeafItemMouseOver}
              onItemMouseClick={this.onItemMouseClick}
            />
          )}
        </div>
        <div className="level-nested-wrapper">
          {nestedLevels.map((level, idx) => (
            <LevelItemsList
              {...levelItemsListProps}
              level={level}
              levelIdx={idx + 1}
              showPromoItems={!hidePromoItems && !inSidebar && (!levelIds.length || level.id === 'highlighted')}
              key={`${level.name}-${level.id}`}
            />
          ))}
          {brandBanners && !hideBrandBanners && (
            <NavigationBanners
              brandBanners={brandBanners}
              brandBannersWideThreshold={brandBannersWideThreshold}
              levelIds={levelIds}
              defaultBrandBannersActive={defaultBrandBannersActive}
              pageParameters={productsPage.pageParameters}
            />
          )}
        </div>
      </div>
    );
  }
}
